import { useTranslation } from 'react-i18next';
import {
  Container,
  FirstRow,
  InputWrapper,
  LeftSide,
  RightSide,
  SelectWrapper,
  Heading,
  NoContentLabel,
  NoContentLabelWrapper,
  CompaniesWrapper,
  Wrapper,
} from './MyModelsPage.styled';
import Input from 'components/Input/Input';
import ViewTypePicker, {
  ViewType,
} from 'components/ViewTypePicker/ViewTypePicker';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDebounce } from 'utils/hooks/useDebounce';
import { Select } from 'components/Select/Select';
import { useSelectedViewType } from 'pages/MyModelsPage/hooks';
import { MagnifyingGlass as Search } from '@phosphor-icons/react';
import { usePageTitle } from 'utils/hooks/usePageTitle';
import { useBreakpointFlag } from 'utils/hooks/useBreakpointFlag';
import { RoutesConfig } from 'navigation/routes';
import { MODELS_PER_PAGE, tableConfig } from './constants';
import { IMyModel, IMyModelsDTO } from 'types/Model.types';
import { useGetMyModels } from './hooks';
import { formatMyModelsTableData } from 'components/Table/tableDataFormatter';
import ModelCard from 'components/ModelCard/ModelCard';
import Table from 'components/Table/Table';
import Pagination from 'components/Pagination/Pagination';
import { useGetCategoryOptions } from 'utils/hooks/useGetCategoryOptions';
import ModelCardSkeleton from 'components/ModelCard/ModelCardSkeleton/ModelCardSkeleton';

const MyModelsPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [page, setPage] = useState<number>(1);
  const { selectedViewType, setSelectedViewType } = useSelectedViewType();
  const [selectedCategoryId, setSelectedCategoryId] = useState<number | null>(
    null
  );

  const [searchBy, setSearchBy] = useState<string>('');
  const debouncedSearchBy = useDebounce(searchBy);
  const [categoriesSearchBy, setCategoriesSearchBy] = useState<string>('');
  const debouncedCategoriesSearchBy = useDebounce(categoriesSearchBy);

  const { categoryOptions, fetchNextPage, hasNextPage } = useGetCategoryOptions(
    debouncedCategoriesSearchBy
  );

  useEffect(() => {
    if (debouncedSearchBy && page !== 1) {
      setPage(1);
    }
  }, [debouncedSearchBy]);

  usePageTitle('My Models');

  const { isMidTablet } = useBreakpointFlag();

  useEffect(() => {
    if (isMidTablet && selectedViewType === ViewType.LIST) {
      setSelectedViewType(ViewType.GRID);
    }
  }, [isMidTablet]);

  const handleOpenModel = (modelId: number) => {
    navigate(
      RoutesConfig.MyModelsSingle.fullPath.replace(':id', modelId.toString())
    );
  };

  const { data: modelsData, isLoading: isLoadingModels } = useGetMyModels(
    page,
    MODELS_PER_PAGE,
    debouncedSearchBy,
    selectedCategoryId
  );

  let tableData: IMyModelsDTO[] = [];
  if (modelsData?.models) {
    tableData = formatMyModelsTableData(
      modelsData?.models
        .map((model: IMyModel[]) => {
          return model;
        })
        .flat(),
      t
    );
  }

  const renderSkeletons = () => {
    return Array.from({ length: MODELS_PER_PAGE }, (_, index) => (
      <ModelCardSkeleton key={index} index={index} opacityCoefficient={0.1} />
    ));
  };

  return (
    <Container>
      <FirstRow>
        <LeftSide>
          <Heading>{t('My Models')}</Heading>
        </LeftSide>
        <RightSide>
          <SelectWrapper>
            <Select
              width="100%"
              defaultValue={{ value: '', label: 'All categories' }}
              name="category-select"
              placeholder={t('Category')}
              options={categoryOptions}
              onChange={(selectedOption: any) =>
                setSelectedCategoryId(selectedOption.value)
              }
              onInputChange={(value: string) => setCategoriesSearchBy(value)}
              onMenuScrollToBottom={() => hasNextPage && fetchNextPage()}
            />
          </SelectWrapper>
          <InputWrapper>
            <Input
              placeholder={t('Search')}
              width={'100%'}
              onChange={(e: any) => setSearchBy(e.target.value)}
              icon={Search}
              wrapperStyles={{ width: '100%' }}
            />
          </InputWrapper>

          {!isMidTablet && (
            <ViewTypePicker
              selectedViewType={selectedViewType}
              setSelectedViewType={setSelectedViewType}
            />
          )}
        </RightSide>
      </FirstRow>

      {!modelsData?.models.length && !isLoadingModels ? (
        <NoContentLabelWrapper>
          <NoContentLabel>{t('No models available')}</NoContentLabel>
        </NoContentLabelWrapper>
      ) : (
        <>
          {selectedViewType === ViewType.GRID ? (
            <Wrapper>
              <CompaniesWrapper>
                {modelsData?.models.map((model: IMyModel, index: number) => (
                  <ModelCard
                    key={`model-card-${index}`}
                    model={model}
                    onOpenModel={(modelId: number) => {
                      handleOpenModel(modelId);
                    }}
                    myModels={true}
                    buttonLabel={t('Open')}
                  />
                ))}
                {isLoadingModels ? renderSkeletons() : null}
              </CompaniesWrapper>
              {modelsData?.pagination?.total > MODELS_PER_PAGE && (
                <Pagination
                  page={page}
                  perPage={MODELS_PER_PAGE}
                  total={modelsData?.pagination?.total}
                  setPage={setPage}
                />
              )}
            </Wrapper>
          ) : (
            <Table
              page={page}
              perPage={MODELS_PER_PAGE}
              total={modelsData?.pagination?.total}
              isLoading={isLoadingModels}
              tableData={tableData}
              tableConfig={tableConfig}
              hasExtraAction={true}
              extraActionLabel={t('Open')}
              loadPage={(newPage: number) => setPage(newPage)}
              onViewButtonClick={(rowData: any) => {
                handleOpenModel(rowData.navigationID);
              }}
            />
          )}
        </>
      )}
    </Container>
  );
};

export default MyModelsPage;
